<template>
  <div
    class="review-previewer"
    :class="{ 'review-previewer--removed': isRemoved }"
  >
    <AkRating
      class="review-previewer__rating"
      :model-value="overallRating"
      read-only
    />
    <div
      v-if="review.is_automatic"
      class="review-previewer__automatic"
      data-testid="reviewAutomaticNotice"
    >
      <p class="review-previewer__automatic-label">
        <AkIcon symbol="info-circle" />
        {{ $t('reviews.previewer.automatic.lbl') }}
      </p>
      <p class="review-previewer__automatic-description">
        {{ $t('reviews.previewer.automatic.description') }}
      </p>
    </div>
    <template v-else>
      <p class="review-previewer__title">
        {{ title }}
      </p>
      <p class="review-previewer__description">
        {{ review.description }}
      </p>
    </template>
    <div class="review-previewer__categories">
      <div
        v-for="category in categories"
        :key="category.title"
        class="review-previewer__category"
      >
        {{ category.title }}
        <span class="review-previewer__category-value">{{ category.value }}</span>
        <span class="review-previewer__category-star"><AkIcon symbol="star" /></span>
      </div>
    </div>
    <slot name="footer" />
    <template v-if="showBrandAnswer && hasBrandAnswer">
      <div class="review-previewer__answer-toggle">
        <AkButton
          link
          size="md"
          symbol="chevron-down"
          symbol-position="right"
          @click="toggleBrandAnswerBlock"
        >
          {{ replyText }}
        </AkButton>
      </div>
      <div class="review-previewer__answer">
        <AkExpander
          :open="isBrandAnswerBlockOpened"
          @toggle="toggleBrandAnswerBlock"
        >
          <div class="brand-answer">
            <section>
              <AkParagraph
                size="xs"
                class="review-previewer__answer-author"
              >
                <img
                  :src="brandImgUrl"
                  alt="brand logo"
                  class="review-previewer__answer-logo"
                >
                <span class="review-previewer__answer-intro">{{ $t('Reply from') }}</span>
                <span class="review-previewer__answer-brand">{{ review.brand_answer.brand_name }} </span>
              </AkParagraph>
            </section>
            <section>
              <AkParagraph
                size="sm"
                class="review-previewer__answer-text"
              >
                {{ review.brand_answer.answer }}
              </AkParagraph>
            </section>
            <section>
              <AkParagraph
                size="xs"
                class="review-previewer__answer-date"
              >
                {{ brandAnswerDate }}
              </AkParagraph>
            </section>
          </div>
        </AkExpander>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { AkIcon } from '@ankorstore/design-system';
import AkRating from '@monolith/legacy/components/review/AkRating/AkRating.vue';
import { formatDate } from '@monolith/legacy/services/date-locale';
import { mapGetters } from 'vuex';
import { Review, ReviewCriteria } from '@monolith/legacy/components/review/types';

export default defineComponent<{ review: Review } & { overallRating: number }>({
  name: 'ReviewPreviewer',
  components: {
    AkIcon,
    AkRating,
  },
  props: {
    review: {
      type: Object as PropType<Review>,
      required: true,
    },
    showBrandAnswer: {
      type: Boolean,
    },
    isRemoved: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  data() {
    return {
      isBrandAnswerBlockOpened: false,
      imgPlaceholder: '/images/reviews/brand-img-placeholder.svg',
    };
  },
  computed: {
    ...mapGetters(['brand']),
    brandId(): number {
      return this.brand?.id;
    },
    overallRating(): number {
      return this.review.ratings[ReviewCriteria.OVERALL];
    },
    title(): string {
      return this.review.title;
    },
    hasBrandAnswer(): boolean {
      return !!this.review.brand_answer;
    },
    brandImgUrl(): string {
      return this.review.brand_answer?.brand_image ?? (this.imgPlaceholder as string);
    },
    brandAnswerDate(): string {
      return formatDate(this.review.brand_answer?.created_at, 'PPP');
    },
    categories(): { title: string; value: string }[] {
      return [
        [this.$t('Accuracy'), ReviewCriteria.ACCURACY],
        [this.$t('Packaging'), ReviewCriteria.PRODUCT_AND_PACKAGING],
        [this.$t('Communication'), ReviewCriteria.COMMUNICATION],
        [this.$t('Quality'), ReviewCriteria.QUALITY],
      ].map(([title, criteria]) => ({
        title,
        value: this.review.ratings[criteria].toFixed(1),
      }));
    },
    brandLoggedInIsSeeingTheirReviews(): boolean {
      return this.brandId === this.review?.brand?.id;
    },
    replyText(): string {
      return this.brandLoggedInIsSeeingTheirReviews ? this.$t('View your answer') : this.$t('View reply');
    },
  },
  methods: {
    toggleBrandAnswerBlock() {
      this.isBrandAnswerBlockOpened = !this.isBrandAnswerBlockOpened;
    },
  },
});
</script>

<style lang="scss" scoped>
.brand-answer {
  @apply ds-flex ds-items-start ds-flex-col ds-justify-start ds-rounded-md ds-bg-warm-white ds-p-4 ds-w-11/12 ds-mx-auto;
}

.review-previewer {
  $self: &;

  &--removed {
    --ak-rating-star-color: theme('colors.neutral.700');

    #{$self}__category-star {
      color: theme('colors.neutral.700');
    }

    #{$self}__title,
    #{$self}__description,
    #{$self}__category {
      @apply ds-text-neutral-700;
    }
  }

  &__rating {
    :deep(.icon--md) {
      font-size: 1.5rem;
    }
  }

  &__title {
    @apply ds-mt-2.5 ds-text-base ds-font-bold sm:ds-mt-1.5 sm:ds-text-lg;
  }

  &__categories {
    @apply ds-flex ds-flex-wrap ds-gap-2;
  }

  &__category {
    @apply ds-flex ds-items-baseline ds-gap-1 ds-rounded-md ds-bg-neutral-100 ds-p-2;
  }

  &__category-value {
    @apply ds-font-bold;
  }

  &__category-star {
    @apply ds-text-ratings;
  }

  &__automatic {
    @apply ds-flex ds-flex-col ds-gap-1 ds-mt-2.5 ds-mb-3 sm:ds-mt-1.5;
  }

  &__automatic-label {
    @apply ds-flex ds-items-center ds-gap-1.5 ds-m-0 ds-text-base ds-font-bold sm:ds-text-lg;
  }

  &__automatic-description {
    @apply ds-m-0 ds-text-sm ds-text-neutral-700;
  }

  &__answer-toggle,
  &__answer {
    @apply ds-mt-4 ds-font-basic ds-text-xs;
  }

  &__answer-author,
  &__answer-text,
  &__answer-date {
    @apply ds-inline-flex ds-items-center;
  }

  &__answer-date {
    @apply ds-mb-0 ds-font-bold;
  }

  &__answer-logo {
    @apply ds-mr-1 ds-block ds-h-5 ds-w-5 ds-rounded-full ds-object-cover;
  }

  &__answer-intro {
    @apply ds-mr-1;
  }

  &__answer-brand {
    @apply ds-font-bold;
  }
}
</style>
